import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, signOut as fbSignOut, type User } from 'firebase/auth';
import {
  DEFAULT_TENANT_ID,
  type AdminRole,
  type AnyAdminRole,
  type TenantFeature,
} from '@kawai/shared';
import {
  attemptEmailPasswordSignIn,
  hasEnrolledTotp,
  type SignInOutcome,
} from '@kawai/auth-bootstrap';
import { auth as firebaseAuth } from './firebase.js';

// ---------- 型 ----------

export type AuthStatus = 'loading' | 'signed-out' | 'signed-in';

export interface AuthState {
  status: AuthStatus;
  user: User | null;
  /** Custom Claims `role == 'admin'` か */
  isAdmin: boolean;
  /** UI 表示用に user.email を string|null で */
  email: string | null;
  /**
   * Phase 5 (T-05): 所属テナント ID。
   * - 新 role の admin は claims.tenantId をそのまま使用
   * - saas-admin (claims.tenantId === null) は `null`
   * - 旧 admin / migration 未済の admin は `DEFAULT_TENANT_ID` にフォールバック
   * - サインアウト時は `null`
   */
  tenantId: string | null;
  /**
   * Phase 5 (T-06): Custom Claims から派生した role。
   * - 新 role: 'saas-admin' | 'tenant-admin' | 'tenant-staff'
   * - 旧 role: 'admin' | 'superadmin'（移行期間中の後方互換）
   * - 未認証 / claims 未設定 / 不明 role: `null`
   */
  role: AnyAdminRole | null;
  /**
   * Phase 5 (T-06): テナントが契約している機能パッケージ一覧。
   * - 新 role の tenant-admin / tenant-staff は claims.features をそのまま使用
   * - saas-admin は全機能アクセス権限を持つが、ここでは claims.features をそのまま返す
   *   （未設定なら空配列）。saas-admin 判定は `role === 'saas-admin'` で行うこと
   * - 旧 role / 未認証は `[]`
   */
  features: TenantFeature[];
  /**
   * S-75: TOTP 第二要素を登録済みか（firebase/auth の multiFactor.enrolledFactors から派生）。
   * - opt-in: 登録/解除は <MfaSettings>（tenant-portal / saas-admin）から管理者が任意に行う
   * - 登録済アカウントはサインイン時に <MfaChallenge> で TOTP を要求される
   * - signed-out / claims 取得前 / 派生失敗時は false
   */
  mfaEnrolled: boolean;
}

const INITIAL_STATE: AuthState = {
  status: 'loading',
  user: null,
  isAdmin: false,
  email: null,
  tenantId: null,
  role: null,
  features: [],
  mfaEnrolled: false,
};

// ---------- Context ----------

interface AuthContextValue extends AuthState {
  /** メール/パスワードでサインイン。
   *  - `{ kind: 'signed-in' }`: 成功（以降 onAuthStateChanged が発火）
   *  - `{ kind: 'mfa-required', resolver }`: MFA 登録済 → SignInPage が challenge を表示
   *  - パスワード誤り等は Firebase の AuthError をそのまま throw（呼出側で error.code 分岐） */
  signIn: (email: string, password: string) => Promise<SignInOutcome>;
  signOut: () => Promise<void>;
  /**
   * Phase 5 (T-06): ID Token を強制 refresh して最新の Custom Claims を反映する。
   * - bootstrap-admin / adminSetRole / saas-admin の impersonation 切替後に呼び出して
   *   状態を即座に更新するために使用（S-07 で必要）
   * - 内部で `user.getIdToken(true)` → `getIdTokenResult()` を実行し、state に再投影
   * - signed-out のときは no-op
   */
  refreshClaims: () => Promise<void>;
}

// テスト容易性のため Context 自体も export（fake value で wrap してテストできる）
export const AuthContext = createContext<AuthContextValue | null>(null);

export type { AuthContextValue };

// ---------- pure: getIdTokenResult から isAdmin を導出 ----------
// 単体テストのため pure 関数として export。
// Phase 5 (T-01) で Custom Claims を新 role 体系に拡張済。
// - 旧: 'admin' | 'superadmin' (Phase 5 移行期間中の後方互換)
// - 新: 'saas-admin' / 'tenant-admin' は admin 権限あり
// - 新: 'tenant-staff' は参照中心の権限。ここでは admin 扱いしない（書込操作 UI は別途 role 判定）
// 機能差分（saas-admin のみがテナント横断操作を行う等）は呼出側で claims.role を直接判定する。
export function deriveIsAdmin(claims: Record<string, unknown> | undefined): boolean {
  const role = claims?.role;
  return (
    role === 'admin' ||
    role === 'superadmin' ||
    role === 'saas-admin' ||
    role === 'tenant-admin'
  );
}

/**
 * Phase 5 (T-05): claims から所属 tenantId を導出。
 *
 * - `saas-admin` (claims.tenantId === null) は `null` を返す
 * - 新 role admin (string tenantId) はそのまま返す
 * - 旧 admin / 未認証 / claims 未設定は `DEFAULT_TENANT_ID` にフォールバック
 *   (Phase 5 移行期間中の運用継続性のため。T-01 migration 完了後は fallback 削除予定)
 */
export function deriveTenantId(
  claims: Record<string, unknown> | undefined,
): string | null {
  if (claims && 'tenantId' in claims) {
    const t = claims.tenantId;
    if (t === null) return null;
    if (typeof t === 'string' && t.length > 0) return t;
  }
  return DEFAULT_TENANT_ID;
}

/**
 * Phase 5 (T-06): claims から role を導出。
 * - 新 / 旧 role 文字列をそのまま返す
 * - claims が無い / role が unknown 値 → null
 *
 * 機能差分の判定（saas-admin のみがテナント横断操作 等）は呼出側で
 * `role === 'saas-admin'` のように直接比較する。
 */
export function deriveRole(
  claims: Record<string, unknown> | undefined,
): AnyAdminRole | null {
  const role = claims?.role;
  if (
    role === 'saas-admin' ||
    role === 'tenant-admin' ||
    role === 'tenant-staff' ||
    role === 'admin' ||
    role === 'superadmin'
  ) {
    return role;
  }
  return null;
}

/**
 * Phase 5 (T-06): claims からテナント契約機能 features を導出。
 * - claims.features が string[] かつ既知 TenantFeature 値のみフィルタして返す
 * - 不明値は除外、claims 自体が未設定 / 配列でない → []
 *
 * saas-admin は features が空でも全機能アクセス権を持つ運用とするため、
 * RequireFeature ガード側で `role === 'saas-admin'` を素通り条件にする。
 */
export function deriveFeatures(
  claims: Record<string, unknown> | undefined,
): TenantFeature[] {
  const raw = claims?.features;
  if (!Array.isArray(raw)) return [];
  const known: TenantFeature[] = ['reservation', 'fuel', 'richmenu', 'promo'];
  const out: TenantFeature[] = [];
  for (const v of raw) {
    if (typeof v === 'string' && (known as string[]).includes(v)) {
      out.push(v as TenantFeature);
    }
  }
  return out;
}

/**
 * Phase 5 (T-06): role が「テナント所属（= 1 つの tenantId 配下）」か判定。
 * RequireFeature ガード等で「tenant 所属だが該当 feature を契約していない」ケースを判定するため。
 *
 * - 'tenant-admin' / 'tenant-staff' / 旧 'admin' / 旧 'superadmin' → true
 * - 'saas-admin' → false（テナント横断、bypass 対象）
 * - null → false
 */
export function isTenantScopedRole(
  role: AnyAdminRole | null,
): role is AdminRole | 'admin' | 'superadmin' {
  return (
    role === 'tenant-admin' ||
    role === 'tenant-staff' ||
    role === 'admin' ||
    role === 'superadmin'
  );
}

// ---------- Provider ----------

export interface AuthProviderProps {
  children: ReactNode;
  /** テスト注入用。本番では undefined（= firebase/auth 実装を使用） */
  authClient?: typeof firebaseAuth;
}

export function AuthProvider({ children, authClient }: AuthProviderProps) {
  const a = authClient ?? firebaseAuth;
  const [state, setState] = useState<AuthState>(INITIAL_STATE);

  const applyUserState = useCallback(async (user: User, forceRefresh: boolean) => {
    // Custom Claims が役割反映後に届くよう、通常はキャッシュされた claims を使う。
    // bootstrap-admin / adminSetRole 直後の再ログインで新しい claims が乗るため、
    // 運用上はこれで十分（明示的な再 sign-in を促す）。
    // forceRefresh=true は refreshClaims() 経由の明示再取得時のみ使用（S-07 impersonation 切替）。
    let claims: Record<string, unknown> | undefined;
    try {
      if (forceRefresh) {
        await user.getIdToken(true);
      }
      const tokenResult = await user.getIdTokenResult();
      claims = tokenResult.claims as Record<string, unknown>;
    } catch (err) {
      // claims 取得失敗は致命ではない（admin でないとして扱う）。ログだけ出す。
      // eslint-disable-next-line no-console
      console.error('[auth] getIdTokenResult failed:', err);
    }
    // S-75: mfaEnrolled は multiFactor(user).enrolledFactors（ローカル参照）のみで決まり、
    // token 取得の成否には依存しない。上の try とは別 try にして
    //  (1) 万一 throw しても applyUserState を reject させない（status:'loading' 固着防止）
    //  (2) token 取得の一過性失敗で登録済みが false に落ちない（登録直後の誤「無効」表示防止）
    // の両方を満たす。
    let mfaEnrolled = false;
    try {
      mfaEnrolled = hasEnrolledTotp(user);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[auth] hasEnrolledTotp failed:', err);
    }
    setState({
      status: 'signed-in',
      user,
      isAdmin: deriveIsAdmin(claims),
      email: user.email,
      tenantId: deriveTenantId(claims),
      role: deriveRole(claims),
      features: deriveFeatures(claims),
      mfaEnrolled,
    });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(a, async (user) => {
      if (!user) {
        setState({
          status: 'signed-out',
          user: null,
          isAdmin: false,
          email: null,
          tenantId: null,
          role: null,
          features: [],
          mfaEnrolled: false,
        });
        return;
      }
      await applyUserState(user, false);
    });
    return unsubscribe;
  }, [a, applyUserState]);

  const signIn = useCallback(
    (email: string, password: string): Promise<SignInOutcome> =>
      attemptEmailPasswordSignIn(a, email, password),
    [a],
  );

  const signOut = useCallback(async () => {
    await fbSignOut(a);
  }, [a]);

  const refreshClaims = useCallback(async () => {
    const user = a.currentUser;
    if (!user) return;
    await applyUserState(user, true);
  }, [a, applyUserState]);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut, refreshClaims }),
    [state, signIn, signOut, refreshClaims],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
